import React, { useCallback } from 'react';

/**
 * Payload encoded into the HTML5 DataTransfer object when an action is
 * dragged from the palette onto the canvas. Consumers parse this on the
 * matching `onDrop`.
 */
export interface DragPayload {
  actionId: string;
}

/** MIME type used by xyflow's drag-from-palette convention. */
export const DRAG_DATA_TYPE = 'application/reactflow';

interface UseDragDropResult<T> {
  /**
   * Event handler factory: returns the `onDragStart` handler for the given
   * item. Encodes `{ actionId }` into the DataTransfer payload using the
   * shared MIME type.
   */
  handleDragStart: (item: T) => (event: React.DragEvent<HTMLElement>) => void;
}

/**
 * Encapsulates the drag-source behaviour used by `ActionPalette` (and any
 * future palette-style component). The drop side is owned by
 * `WorkflowCanvas` — they communicate via the shared `DRAG_DATA_TYPE`.
 *
 * Pure: no useState, no useEffect. The hook only memoises the handler so
 * callers can pass it directly into `<div onDragStart={...} />` without
 * forcing parent re-renders.
 */
export const useDragDrop = <T extends { actionId: string }>(): UseDragDropResult<T> => {
  const handleDragStart = useCallback(
    (item: T) => (event: React.DragEvent<HTMLElement>) => {
      event.dataTransfer.setData(DRAG_DATA_TYPE, JSON.stringify({ actionId: item.actionId }));
      // eslint-disable-next-line no-param-reassign
      event.dataTransfer.effectAllowed = 'move';
    },
    [],
  );

  return { handleDragStart };
};
